import type { Database } from "@/lib/database.types";
import { getPlanColor } from './colors';
import { formatCurrency } from './currencyFormatter';
import { useCurrencyStore } from '@/stores/currencyStore';
import type { Category as CategoryType } from '@/features/categories/types';

export type Provider = {
  id: number;
  name: string;
  has_sequence_numbers: boolean;
};

export type Version = Database['public']['Tables']['provider_versions']['Row'];

// Export ChannelNumber type
export type ChannelNumber = {
  type_id: number;
  number: string;
  type?: { id: number; name: string } | null;
};

export type PlanWithColor = {
  id: number;
  name: string;
  color_index: number;
};

// Export Channel type
export type Channel = Database['public']['Tables']['channels']['Row'] & {
  numbers?: ChannelNumber[];
  plans?: Array<{
    plan?: PlanWithColor | null;
  }>;
  price?: number | null;
  description?: string | null;
  category?: Category;
  is_local?: boolean;
  categories?: Array<{
    id: number;
    category_id: number;
    is_default: boolean;
    category: Category;
  }>;
};

// Export Category type with parent_name for breadcrumb display
export type Category = CategoryType & {
  parent_name?: string;
};

// Export SimplifiedPlan type
export interface SimplifiedPlan {
  id: number;
  name: string;
  provider_id: number;
  version_id: number;
  monthly_price: number | null;
  description: string | null;
  yearly_price: number | null;
  color_index: number;
  created_at: string;
  channel_count?: number;
  channels?: Array<{
    channel?: Record<string, any> | null;
  }>;
}

// Filter options type for controlling what's displayed
export interface DisplayFilterOptions {
  // Channel filters
  showDescription: boolean;
  showPrice: boolean;
  showPlans: boolean;
  showInAllCategories: boolean;
  showLocalChannelsCategory: boolean;
  hideSerialNumbers: boolean; // Option to hide serial numbers for sequential providers
  sortOrder?: 'asc' | 'desc'; // Sorting order for channels

  // Plan filters
  showPlanDescription?: boolean;
  showPlanMonthlyPrice?: boolean;
  showPlanYearlyPrice?: boolean;

  // Category filters
  showCategoryDescription?: boolean;
  showCategoryPrice?: boolean;
}

export function formatPreviewData(
  provider: Provider,
  version: Version,
  plans: Array<{
    id: number;
    name: string;
    color_index: number;
    channel_count?: number;
    description?: string | null;
    monthly_price?: number | null;
    yearly_price?: number | null;
  }>,
  categories: Category[],
  channels: Channel[],
  filterOptions: DisplayFilterOptions,
  channelTypes?: Array<{ id: number; name: string }>
): string {
  // Get the currency for this specific provider
  const providerCurrency = useCurrencyStore.getState().getProviderCurrency(provider.id);
  
  let output = '';

  // Add provider and version information
  output += `<!-- wp:paragraph -->\n`;
  output += `<p><strong>${provider.name}</strong>`;
  
  if (version.version_number) {
    output += ` - Version ${version.version_number}`;
  }
  
  if (version.effective_date) {
    const effective = new Date(version.effective_date);
    output += ` (Effective: ${effective.toLocaleDateString()})`;
  }
  
  output += `</p>\n`;
  output += `<!-- /wp:paragraph -->\n\n`;

  // Plan Legend Section
  if (plans.length > 0) {
    output += `<!-- wp:heading {"level":3} -->\n`;
    output += `<h3>Plan Legend</h3>\n`;
    output += `<!-- /wp:heading -->\n\n`;

    // Start plan legend table
    output += `<!-- wp:table {} -->\n`;
    output += `<figure class="wp-block-table"><table>\n`;
    
    // Plan legend header
    output += `<thead>\n<tr>\n`;
    output += `<td>Color</td>\n`;
    output += `<td>Plan Name</td>\n`;
    
    if (filterOptions.showPlanMonthlyPrice) {
      output += `<td>Monthly Price</td>\n`;
    }
    
    if (filterOptions.showPlanYearlyPrice) {
      output += `<td>Yearly Price</td>\n`;
    }
    
    if (filterOptions.showPlanDescription) {
      output += `<td>Description</td>\n`;
    }
    
    output += `<td>Channels</td>\n`;
    output += `</tr>\n</thead>\n`;
    
    output += `<tbody>\n`;
    plans.forEach((plan) => {
      const color = getPlanColor(plan.color_index);
      output += `<tr>\n`;
      
      // Color cell
      output += `<td>`;
      output += `<span style="display:inline-block;width:16px;height:16px;border-radius:50%;background-color:${color.light};border:1px solid ${color.dark};"></span>`;
      output += `</td>\n`;
      
      // Plan name cell
      output += `<td>${plan.name}</td>\n`;
      
      // Add additional cells based on filter options
      if (filterOptions.showPlanMonthlyPrice) {
        output += `<td>${formatCurrency(plan.monthly_price, providerCurrency)}</td>\n`;
      }
      
      if (filterOptions.showPlanYearlyPrice) {
        output += `<td>${formatCurrency(plan.yearly_price, providerCurrency)}</td>\n`;
      }
      
      if (filterOptions.showPlanDescription) {
        output += `<td>${plan.description || '-'}</td>\n`;
      }
      
      // Channel count cell
      output += `<td>${plan.channel_count || 0}</td>\n`;
      
      output += `</tr>\n`;
    });
    
    output += `</tbody>\n`;
    output += `</table></figure>\n`;
    output += `<!-- /wp:table -->\n\n`;
  }

  // Get all available channel types if not provided
  const types = channelTypes || Array.from(
    new Set(
      channels
        .flatMap(channel => channel.numbers || [])
        .filter(number => number.type)
        .map(number => number.type!)
    )
  );

  console.log("Channel types for WordPress table:", types);

  // Function to create a compatible WordPress table
  const createWordPressTable = (categoryName: string, categoryChannels: Channel[], category?: Category) => {
    let tableOutput = '';
    
    // Add category heading as H3
    tableOutput += `<!-- wp:heading {"level":3} -->\n`;
    tableOutput += `<h3>${categoryName}</h3>\n`;
    tableOutput += `<!-- /wp:heading -->\n\n`;

    // Add category price and description if available and filters enabled
    if (category) {
      if (filterOptions.showCategoryPrice && category.price !== null) {
        tableOutput += `<!-- wp:paragraph -->\n`;
        tableOutput += `<p><strong>Category Price:</strong> ${formatCurrency(category.price, providerCurrency)}</p>\n`;
        tableOutput += `<!-- /wp:paragraph -->\n\n`;
      }
      
      if (filterOptions.showCategoryDescription && category.description) {
        tableOutput += `<!-- wp:paragraph -->\n`;
        tableOutput += `<p><strong>Category Description:</strong> ${category.description}</p>\n`;
        tableOutput += `<!-- /wp:paragraph -->\n\n`;
      }
    }

    // Filter out serial number type if hideSerialNumbers is enabled for sequential providers
    const displayTypes = (provider.has_sequence_numbers && filterOptions.hideSerialNumbers) 
      ? types.slice(1) // Skip the first type (serial number) for sequential providers
      : types;

    // Start table block with HTML-compliant format
    tableOutput += `<!-- wp:table {} -->\n`;
    tableOutput += `<figure class="wp-block-table"><table>\n`;
    
    // Table header
    tableOutput += `<thead>\n<tr>\n`;
    
    // Add channel type headers first, filtered based on hideSerialNumbers setting
    displayTypes.forEach(type => {
      tableOutput += `<th>${type.name}</th>\n`;
    });
    
    // Then add channel name header
    tableOutput += `<th>Channel Name</th>\n`;
    
    // Add other headers based on filter options
    if (filterOptions.showPrice) {
      tableOutput += `<th>Price</th>\n`;
    }
    
    if (filterOptions.showDescription) {
      tableOutput += `<th>Description</th>\n`;
    }
    
    if (filterOptions.showPlans) {
      tableOutput += `<th>Plans</th>\n`;
    }
    
    tableOutput += `</tr>\n</thead>\n`;

    // Sort channels by sequence number if available
    const sortedChannels = provider.has_sequence_numbers
      ? [...categoryChannels].sort((a, b) => {
          // For sequential providers, types[0] is the sequence type (ordered by display_order in DB)
          const firstTypeId = types[0]?.id;
          const aSequenceNumber = a.numbers?.find(n => n.type?.id === firstTypeId);
          const bSequenceNumber = b.numbers?.find(n => n.type?.id === firstTypeId);
          const aNum = aSequenceNumber?.number || '0';
          const bNum = bSequenceNumber?.number || '0';
          const comparison = parseInt(aNum, 10) - parseInt(bNum, 10);
          // Apply sort order (asc or desc)
          return filterOptions.sortOrder === 'desc' ? -comparison : comparison;
        })
      : categoryChannels;

    // Table body
    tableOutput += `<tbody>\n`;
    sortedChannels.forEach(channel => {
      tableOutput += `<tr>\n`;
      
      // Include channel types/numbers, filtered based on hideSerialNumbers setting
      displayTypes.forEach(type => {
        const numberForType = channel.numbers?.find(n => n.type?.id === type.id);
        tableOutput += `<td>${numberForType?.number || '-'}</td>\n`;
      });

      // Then add channel name
      tableOutput += `<td>${channel.name}</td>\n`;
      
      // Conditionally add other cells based on filter options
      if (filterOptions.showPrice) {
        tableOutput += `<td>${formatCurrency(channel.price, providerCurrency)}</td>\n`;
      }
      
      if (filterOptions.showDescription) {
        tableOutput += `<td>${channel.description || '-'}</td>\n`;
      }
      
      // Plans - Show colored circles with title attribute showing plan names
      if (filterOptions.showPlans) {
        if (!channel.plans || channel.plans.length === 0) {
          tableOutput += `<td>-</td>\n`;
        } else {
          tableOutput += `<td>`;
          channel.plans.forEach(planObj => {
            if (planObj.plan) {
              const plan = planObj.plan;
              const color = getPlanColor(plan.color_index);
              // Add title attribute with plan name for accessibility and hover tooltip
              tableOutput += `<span title="${plan.name}" style="display:inline-block;width:12px;height:12px;border-radius:50%;background-color:${color.light};border:1px solid ${color.dark};margin-right:4px;"></span>`;
            }
          });
          tableOutput += `</td>\n`;
        }
      }
      
      tableOutput += `</tr>\n`;
    });
    
    tableOutput += `</tbody>\n`;
    tableOutput += `</table></figure>\n`;
    tableOutput += `<!-- /wp:table -->\n\n`;

    return tableOutput;
  };;

  // Group channels by category for WordPress export
  const channelsByCategory: Record<string | number, Channel[]> = {};
  
  // Add a collection for local channels if the option is enabled
  const localChannels: Channel[] = [];
  
  // Organize channels by categories, respecting the filterOptions.showInAllCategories flag
  channels.forEach(channel => {
    // Check if this is a local channel and filter option is enabled
    if (filterOptions.showLocalChannelsCategory && channel.is_local) {
      localChannels.push(channel);
      
      // If we don't want the channel to appear in all categories,
      // we can return here to skip adding it to regular categories
      if (!filterOptions.showInAllCategories) {
        return;
      }
    }
    
    // Handle multiple categories (new approach)
    if (channel.categories && channel.categories.length > 0) {
      // Decide which categories to use based on the showInAllCategories flag
      const categoriesToUse = filterOptions.showInAllCategories 
        ? channel.categories 
        : channel.categories.filter((cc: {is_default: boolean}) => cc.is_default);
      
      categoriesToUse.forEach((channelCategory: {category: Category; category_id: number}) => {
        if (channelCategory.category) {
          const categoryId = channelCategory.category.id;
          if (!channelsByCategory[categoryId]) {
            channelsByCategory[categoryId] = [];
          }
          channelsByCategory[categoryId].push(channel);
        }
      });
    } 
    // Legacy fallback for single category_id field
    else if (channel.category_id) {
      const categoryId = channel.category_id;
      if (!channelsByCategory[categoryId]) {
        channelsByCategory[categoryId] = [];
      }
      channelsByCategory[categoryId].push(channel);
    } 
    // Uncategorized channels
    else {
      if (!channelsByCategory['uncategorized']) {
        channelsByCategory['uncategorized'] = [];
      }
      channelsByCategory['uncategorized'].push(channel);
    }
  });

  // Format as WordPress Gutenberg table blocks for each category in hierarchical order
  categories.forEach(category => {
    const categoryChannels = channelsByCategory[category.id] || [];

    if (categoryChannels.length > 0) {
      const isSubcategory = category.parent_id !== null;
      const categoryTitle = isSubcategory && category.parent_name 
        ? `${category.parent_name} > ${category.name}` 
        : category.name;
      output += createWordPressTable(categoryTitle, categoryChannels, category);
    }
  });

  // Add local channels section if option is enabled and local channels exist
  if (filterOptions.showLocalChannelsCategory && localChannels.length > 0) {
    output += createWordPressTable("Local Channels", localChannels);
  }

  // After the existing categories loop, add uncategorized channels
  const uncategorizedChannels = channelsByCategory['uncategorized'] || [];
  
  if (uncategorizedChannels.length > 0) {
    output += createWordPressTable("Uncategorized Channels", uncategorizedChannels);
  }

  return output;
}
